import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, ArrowUp, ArrowDown, Gift, Coins, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { apiService } from '../services/api.service';

interface Transaction {
  id: string;
  organization_id: string;
  org_name: string;
  event_id: string | null;
  event_name: string | null;
  type: string;
  tokens_amount: number;
  balance_after: number;
  price_per_token_cents: number | null;
  total_price_cents: number | null;
  discount_applied_cents: number | null;
  tier_slug: string | null;
  description: string;
  created_at: string;
}

interface Summary {
  total_transactions: number;
  total_tokens_purchased: number;
  total_revenue_cents: number;
  total_tokens_consumed: number;
  total_tokens_gifted: number;
  total_welcome_tokens: number;
  paying_organizations: number;
  welcome_organizations: number;
}

const AdminTransactions: React.FC = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transRes, summaryRes] = await Promise.all([
        apiService.getAdminTransactions({ limit: 200 }),
        apiService.getAdminTransactionsSummary(),
      ]);
      setTransactions(transRes.transactions || []);
      setSummary(summaryRes);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = filterType === 'all'
    ? transactions
    : transactions.filter(tx => tx.type === filterType);

  const typeColor = (type: string) => {
    switch (type) {
      case 'purchase': return 'bg-white/[0.06] text-muted-foreground';
      case 'welcome_bonus': return 'bg-white/[0.06] text-muted-foreground';
      case 'consume': return 'bg-white/[0.06] text-muted-foreground';
      case 'gift': return 'bg-white/[0.06] text-muted-foreground';
      case 'refund': return 'bg-white/[0.06] text-muted-foreground';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case 'purchase': return t('admin.transactions.typePurchase');
      case 'welcome_bonus': return t('admin.transactions.typeWelcome');
      case 'consume': return t('admin.transactions.typeConsume');
      case 'gift': return t('admin.transactions.typeGift');
      case 'refund': return t('admin.transactions.typeRefund');
      default: return type;
    }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Organisation', 'Type', 'Jetons', 'Solde après', 'Event', 'Prix total', 'Description'];
    const rows = filteredTransactions.map(tx => [
      new Date(tx.created_at).toLocaleDateString(),
      tx.org_name,
      tx.type,
      tx.tokens_amount,
      tx.balance_after,
      tx.event_name || '',
      tx.total_price_cents ? `${(tx.total_price_cents / 100).toFixed(2)}` : '',
      tx.description || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'token-transactions.csv';
    a.click();
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-3xl font-bold">{t('admin.transactions.title')}</h1>
        <Button variant="outline" size="sm" onClick={exportCSV} className="w-full sm:w-auto">
          <Download size={16} className="mr-1" />
          CSV
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground uppercase">{t('admin.transactions.totalRevenue')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                {(summary.total_revenue_cents / 100).toFixed(0)}€
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.total_tokens_purchased} {t('myLicense.tokens')} · {summary.paying_organizations} orgas
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground uppercase">{t('admin.transactions.tokensConsumed')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground flex items-center gap-1">
                <Coins size={20} />
                {summary.total_tokens_consumed}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground uppercase">{t('admin.transactions.tokensGifted')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground flex items-center gap-1">
                <Gift size={20} />
                {summary.total_tokens_gifted}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground uppercase">{t('admin.transactions.welcomeOrgs')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground flex items-center gap-1">
                <Users size={20} />
                {summary.welcome_organizations}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.total_welcome_tokens} {t('myLicense.tokens')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 md:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.transactions.allTypes')}</SelectItem>
            <SelectItem value="purchase">{t('admin.transactions.typePurchase')}</SelectItem>
            <SelectItem value="consume">{t('admin.transactions.typeConsume')}</SelectItem>
            <SelectItem value="gift">{t('admin.transactions.typeGift')}</SelectItem>
            <SelectItem value="welcome_bonus">{t('admin.transactions.typeWelcome')}</SelectItem>
            <SelectItem value="refund">{t('admin.transactions.typeRefund')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.transactions.date')}</TableHead>
                  <TableHead>{t('admin.transactions.organization')}</TableHead>
                  <TableHead>{t('admin.transactions.type')}</TableHead>
                  <TableHead className="text-center">{t('admin.transactions.tokens')}</TableHead>
                  <TableHead className="text-center">{t('admin.transactions.balanceAfter')}</TableHead>
                  <TableHead>{t('admin.transactions.event')}</TableHead>
                  <TableHead>{t('admin.transactions.price')}</TableHead>
                  <TableHead>{t('admin.transactions.description')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      {t('admin.transactions.noTransactions')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-muted-foreground whitespace-nowrap text-xs">
                        {new Date(tx.created_at).toLocaleDateString()}{' '}
                        {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{tx.org_name}</TableCell>
                      <TableCell>
                        <Badge className={`${typeColor(tx.type)} text-xs`}>
                          {typeLabel(tx.type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-bold ${tx.tokens_amount > 0 ? 'text-success' : 'text-foreground'}`}>
                          {tx.tokens_amount > 0 ? '+' : ''}{tx.tokens_amount}
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground text-xs">
                        {tx.balance_after}
                      </TableCell>
                      <TableCell className="text-sm">{tx.event_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {tx.total_price_cents ? (
                          <span className="text-foreground">{(tx.total_price_cents / 100).toFixed(0)}€</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                        {tx.description || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminTransactions;
